import { formatDate } from 'utils'

const Y_AXIS_FORMAT = {
  percentage: {
    metrics: ['completedPC', 'notCompletedPC', 'uniqPathSessionPC'],
    format: value => `${(value * 100).toFixed(0)}%`,
  },
  money: {
    metrics: ['balance', 'credits', 'cost', 'min', 'max', 'avg', 'rps', 'rpus', 'rpu', 'rpi'],
    format: value => `${value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`,
  },
}

const resolveDateLabel = item => item.formattedKey || (item.value != null ? formatDate(parseInt(item.value), 'MM/DD') : item.value)

export const collectLeafRows = node => {
  if (!node) return []
  if (node.children) return node.children.flatMap(collectLeafRows)
  return [node]
}

export const extractBarChartData = ({ data, metrics, endDate }) => {
  const children = collectLeafRows(data)
  const { format, type } = getFormatType(metrics[0])

  return {
    data: children.map((item, index) => {
      const dateStart = Number(item.value)
      const dateEnd = Number(children[index + 1]?.value) || endDate?.valueOf()

      return metrics.reduce((acc, metric) => {
        acc[metric] = Number(item[metric]) || 0
        return acc
      }, {
        date: resolveDateLabel(item),
        dateStart: Number.isFinite(dateStart) ? dateStart : null,
        dateEnd: Number.isFinite(dateEnd) ? dateEnd : null,
      })
    }),
    keys: metrics,
    format,
    type,
  }
}

const getFormatType = metric => {
  if (Y_AXIS_FORMAT.percentage.metrics.includes(metric)) {
    return {
      format: Y_AXIS_FORMAT.percentage.format,
      type: 'percentage',
    }
  }

  if (Y_AXIS_FORMAT.money.metrics.includes(metric)) {
    return {
      format: Y_AXIS_FORMAT.money.format,
      type: 'money',
    }
  }

  return {
    format: value => {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1).replace('.0', '')}M`
      } else if (value >= 1000) {
        return `${(value / 1000).toFixed(1).replace('.0', '')}k`
      } else {
        return value.toString()
      }
    },
    type: 'count',
  }
}

const TIME_INTERVALS = {
  M: {
    time: 1000 * 60 * 60 * 24 * 30,
    format: 'M y',
    groupby: 'month',
  },
  W: {
    time: 1000 * 60 * 60 * 24 * 7,
    format: 'w - y',
    groupby: 'week',
  },
  d: {
    time: 1000 * 60 * 60 * 24,
    format: 'd-MMM',
    groupby: 'day',
  },
  h: {
    time: 1000 * 60 * 60,
    format: 'd ha',
    groupby: 'hour',
  },
  m: {
    time: 1000 * 60,
    format: 'h:m a',
    groupby: 'minute',
  },
}

const getMaximumPoints = ({ startDate, endDate }) => {
  const daysDiff = endDate.diff(startDate, 'days')
  const monthsDiff = endDate.diff(startDate, 'months')

  if (monthsDiff > 0 && monthsDiff < 7) return 31

  if (daysDiff <= 7) return 24

  return 12
}

export const getGroupByWithDateInterval = ({ startDate, endDate }) => {
  const diff = endDate - startDate

  const differences = {
    M: Math.round(diff / TIME_INTERVALS.M.time),
    W: Math.round(diff / TIME_INTERVALS.W.time),
    d: Math.round(diff / TIME_INTERVALS.d.time),
    h: Math.round(diff / TIME_INTERVALS.h.time),
  }

  const maxPoints = getMaximumPoints({ startDate, endDate })
  for (const difference in differences) {
    if (Object.prototype.hasOwnProperty.call(differences, difference)) {
      const differencePoint = Math.floor(differences[difference] / maxPoints)
      if (differencePoint > 0) {
        return `${TIME_INTERVALS[difference].groupby}:${differencePoint === 0 ? differencePoint + 1 : differencePoint} ${encodeURIComponent(TIME_INTERVALS[difference].format)}`
      }
    }
  }

  return 'hour:1 ha'
}
